// docs/77 NF3 — what becomes of a named foe on the board, after the module's secret plot-armour
// roll (`foe_fate`, written the pulse they drop):
//
// FOE DEFEATED (the armour failed): once that pulse has played out, a celebration card over the
// board — "Foe Defeated!", their face and name, how many times they had got away before, and a
// Continue. Once per encounter per browser.
//
// The getaway (the armour held) is the opening's machinery: the director publishes the foe and how
// they go (Opening.tsx), the scene plays it in place of a fall and the leader barks as they go.
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Button, HotkeyKbd, Portrait, useReducedMotion } from '../components';
import { useEncounter } from '../../core/encounter/session';
import { usePlayback } from '../../core/stores/playback';
import { useCinematic } from '../../core/stores/cinematic';
import { FATE, useFoeFate } from '../../core/subscriptions/hooks';
import { useMusicMood } from '../../core/audio/music';
import { playSfx } from '../../core/audio';
import type { Escape } from '../../core/story/genesis';
import { isHotkey } from '../../core/input/hotkeys';
import './named-foe.css';

/** How a named foe other than the World's first gets away: a stock getaway, fixed by their npc. */
const GETAWAYS: Escape[] = ['Smoke', 'Shadow', 'Steed'];
export const getawayOf = (npc: bigint): Escape => GETAWAYS[Number(npc % BigInt(GETAWAYS.length))] ?? 'Smoke';

/** What a named foe barks as they get away (docs/77), or as a quest leader who got away rises as one. */
export const GETAWAY_LINE = 'You have not seen the last of me.';
export const RISEN_LINE = 'Remember my name. We will meet again.';

/** The card as a pure view (the live HUD and the `?foedefeated=1` harness). */
export function FoeDefeatedView({ name, survivals, heroes, frame, onDone }: { name: string; survivals: number; heroes: string[]; frame?: number; onDone: () => void }) {
  const reduced = useReducedMotion();
  useMusicMood('triumph', true);
  useEffect(() => { playSfx('cine.victory'); }, []);
  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (isHotkey(e, 'card.dismiss')) { e.preventDefault(); e.stopImmediatePropagation(); onDone(); } };
    window.addEventListener('keydown', h, true);
    return () => window.removeEventListener('keydown', h, true);
  }, [onDone]);
  const before = survivals === 0 ? 'Struck down for good.' : survivals === 1 ? 'They got away from you once. Not this time.' : `They got away from you ${survivals} times. Not this time.`;
  const by = heroes.length ? heroes.length === 1 ? heroes[0] : `${heroes.slice(0, -1).join(', ')} and ${heroes[heroes.length - 1]}` : 'The heroes';
  return createPortal(
    <div className={`fk-foe-defeated ${reduced ? 'fk-foe-defeated--still' : ''}`} role="dialog" aria-modal="true" aria-label={`Foe defeated: ${name}`} data-testid="foe-defeated">
      <div className="fk-foe-defeated__rays" aria-hidden="true" />
      <div className="fk-foe-defeated__card">
        <span className="fk-foe-defeated__eyebrow">Foe Defeated!</span>
        <span className="fk-foe-defeated__face"><Portrait frame={frame} scale={4} title={name} /></span>
        <span className="fk-foe-defeated__name" data-testid="foe-defeated-name">{name}</span>
        <span className="fk-foe-defeated__note">{before}</span>
        <span className="fk-foe-defeated__by">{by} brought them down, and they will not rise again.</span>
        <Button variant="primary" onClick={onDone} data-testid="foe-defeated-continue" autoFocus>Continue <HotkeyKbd id="card.dismiss" /></Button>
      </div>
    </div>,
    document.body,
  );
}

/**
 * The live card: the encounter's named foe failed their plot-armour roll, and the pulse that
 * dropped them has played out (the board has caught up and nothing is playing back).
 */
export function FoeDefeated() {
  const encounterId = useEncounter((s) => s.encounterId);
  const fate = useFoeFate(encounterId);
  const caughtUp = useEncounter((s) => !!s.snapshot && s.boardSnapshot === s.snapshot);
  const playing = usePlayback((s) => s.playing);
  const snapshot = useEncounter((s) => s.snapshot);
  const heroNames = useEncounter((s) => s.heroNames);
  const seen = useCinematic((s) => s.felledSeen);
  const markSeen = useCinematic((s) => s.setFelledSeen);
  const faceoff = useCinematic((s) => s.faceoffPlaying);
  useCinematic((s) => s.rev);
  const heroes = useMemo(() => Object.values(heroNames), [heroNames]);
  // the board no longer carries them once they are down: keep the face we saw
  const frame = useRef<number | undefined>(undefined);
  const leader = snapshot?.units.find((u) => u.faction === 'enemy' && fate && u.name?.trim().toLowerCase() === fate.name.trim().toLowerCase());
  if (leader?.appearance) frame.current = leader.appearance;
  const onDone = useCallback(() => { if (encounterId !== undefined) markSeen(encounterId); }, [markSeen, encounterId]);
  if (!fate || fate.fate !== FATE.DEFEATED || encounterId === undefined || !caughtUp || playing || faceoff || seen(encounterId)) return null;
  return <FoeDefeatedView name={fate.name} survivals={fate.survivals} heroes={heroes} frame={leader?.appearance ?? frame.current} onDone={onDone} />;
}
